import { useMemo } from "react";
import { useWidgetViewState } from "@salestrak/ui";
import { resolveDatePreset, type ResolvedDateRange } from "#app/lib/utils/date-presets";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { DashboardScope } from "./dashboard-scope-context";
import {
  useEffectiveScope,
  type ScopedWhereOptions,
} from "./scope-shared";
import { useDateBoundaryKey } from "#app/lib/hooks/use-date-boundary-key";

export type ScopedWhere = {
  /**
   * The merged issue `where`: the widget's own `base` AND the dashboard's
   * `entityWhere` AND the (effective) date window AND the member set AND Only-Me —
   * ready to drop straight into a query's `where` variable. Always an object (never
   * null), and stable across renders (recomputes only when the scope or base changes).
   */
  where: Record<string, unknown>;
  /** The resolved `{ startDate, endDate }` of the active window — for axis labels,
   *  "vs. last period" copy, or any widget that needs the literal dates. */
  dateRange: ResolvedDateRange;
  /** The effective scope, never null (the default window fills in off-dashboard;
   *  any per-widget override is already folded in). */
  scope: DashboardScope;
};

/**
 * THE canonical scope→`where` fold every issue-backed widget uses. It reads the
 * ambient {@link DashboardScope} (falling back to a default window off-dashboard),
 * applies any per-widget `override`, resolves the date preset, and AND-merges the
 * universal data window (date + members + Only-Me) with the dashboard's `entityWhere`
 * and the widget's own `base` predicate into one memoized `where`. It also ANDs the
 * widget's transient header `filterWhere` from `WidgetViewState` (written by the
 * shared header filter control; empty off-card, so drilldown/detail-dialog readers
 * are unaffected). A widget NEVER folds the scope by hand — it calls this and drops
 * the result into its query variables.
 * (Activity-backed widgets use the sibling `useScopedActivityWhere` — same window, the
 * `ActivityWhereInput` shape.)
 *
 * ```ts
 * const { where } = useScopedWhere();                        // the whole window
 * const { where } = useScopedWhere({ stageId: { eq: id } }); // window AND this stage
 * const { where } = useScopedWhere(base, { override: { dateRange } }); // own date range
 * ```
 *
 * `base`/`override` may be passed inline (a fresh object each render) — the result is
 * memoized off a stable serialization, so they do not churn the query variables.
 */
export function useScopedWhere(
  base?: Record<string, unknown> | null,
  opts?: ScopedWhereOptions,
): ScopedWhere {
  const { scope, effDateRange, effMemberIds, effOnlyMe } = useEffectiveScope(opts?.override);
  const viewState = useWidgetViewState();
  const filterWhere = viewState.state.filterWhere as Record<string, unknown> | null | undefined;
  const dateField = opts?.dateField === undefined ? "creationDate" : opts.dateField;
  // Relative presets resolve against "now"; fold a day-boundary signal into the
  // memo key so they re-resolve when the local day rolls over (B8). Absolute
  // presets (all-time/custom) don't depend on now, so they skip it and never
  // churn their query variables at midnight.
  const dayKey = useDateBoundaryKey();
  const dateIsRelative = effDateRange.key !== "all-time" && effDateRange.key !== "custom";

  // Stable serializations of exactly the (effective) inputs the merge reads. The
  // merge recomputes only when one of these strings changes — so an inline `base`,
  // a freshly-built `entityWhere` ref, or an inline `override` never forces a new
  // `where` (which would re-subscribe every consuming `useQuery` each render). With
  // `override` omitted the effective values equal `scope.*`, so `scopeKey` is
  // byte-identical to the pre-override fold: existing widgets' `where` identity is kept.
  const baseKey = base ? JSON.stringify(base) : "";
  const scopeKey = JSON.stringify({
    e: scope.entityWhere,
    k: effDateRange.key,
    c: effDateRange.custom ?? null,
    m: effMemberIds,
    o: effOnlyMe,
    f: dateField,
    t: filterWhere ?? null,
    d: dateIsRelative ? dayKey : "",
  });

  return useMemo<ScopedWhere>(() => {
    const dateRange = resolveDatePreset(effDateRange.key, effDateRange.custom);
    const conditions: Record<string, unknown>[] = [];
    if (base) conditions.push(base);
    if (scope.entityWhere) conditions.push(scope.entityWhere);
    if (filterWhere && Object.keys(filterWhere).length > 0) conditions.push(filterWhere);
    // "all-time" means no date bound — drop the clause entirely rather than
    // AND-ing in an epoch→now range the API would still have to scan.
    if (dateField && effDateRange.key !== "all-time") {
      conditions.push({ [dateField]: { gte: dateRange.startDate, lte: dateRange.endDate } });
    }
    if (effMemberIds.length > 0) {
      conditions.push({ assignees: { entityId: { hasSome: effMemberIds } } });
    }
    const merged = combineWhereConditions(conditions) ?? {};
    // Only-Me is a top-level predicate the API reads off the `where` root (it
    // mirrors the issue screens' `applyCurrentUserFilter`), so it merges in at
    // the root rather than as another AND branch.
    const where = effOnlyMe ? { ...merged, onlyCurrentUser: true } : merged;
    // Return the EFFECTIVE window so a widget reading `.scope`/`.dateRange` for axis
    // labels sees its own overridden values, not the ambient dashboard window.
    const effectiveScope: DashboardScope = {
      ...scope,
      dateRange: effDateRange,
      memberIds: effMemberIds,
      onlyMe: effOnlyMe,
    };
    return { where, dateRange, scope: effectiveScope };
    // baseKey/scopeKey capture every value the body reads; depending on the raw
    // objects instead would recompute every render (inline `base`, fresh refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, scopeKey]);
}
